import useSWR from 'swr';
import axios from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { GridColDef, GridSelectionModel } from '@mui/x-data-grid-pro';
import { useLocation, useNavigate } from 'react-router-dom';
import { SERVICES } from '@shared/constants/apiConfig';
import { ContactManagerTabs } from '@shared/constants/constants';
import { formatDate, formatPhoneNumber, queryParams, withQueryParams } from '@shared/commonFunctions';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { ILcQueueProps } from '@modules/admin/LCMenu/MyLCQueues/types';
import { dataGridCol, gridCurrencyCell } from '@shared/components/grid/common';
import { dateFormatWithTime } from '@shared/constants/formats';
import { ViewHeightWrapper } from '@styles/CommonStyles';
import { useTabs } from '@shared/hooks/useTabs';
import { useServerSide } from '@shared/hooks/useServerSide';
import { ISearchData, SearchItem, SearchResponse } from './types';

export const SearchResult = ({ title }: ILcQueueProps) => {
  const params = queryParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { updateCurrentTab, openTab } = useTabs();

  const pageSize = 50;
  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);
  const [items, setItems] = useState<SearchItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const { requestPayload, onSortModelChange, onFilterModelChange, onPageChange } = useServerSide();
  useEffect(
    () => {
      updateCurrentTab({ 
        route: `${location.pathname.toString()}${location.search}`, 
        title
      });
    },
    [location.pathname, location.search, title]
  );

  const searchPayload = {
    searchText: params.searchText,
    pagination: { ...requestPayload, pageSize: pageSize }
  };

  const swrKey = JSON.stringify(searchPayload);

  const { data: searchResponse, isValidating, error } = useSWR<SearchResponse>(
    swrKey,
    (key: string) => {
      const url = withQueryParams(`/${SERVICES.SEARCH}/search/sorted/search`);
      return axios.post(url, JSON.parse(key));
    }, { revalidateOnFocus: false, revalidateIfStale: false}
  );

  useEffect(() => {
    if (searchResponse) {
      setItems(searchResponse?.data?.items);
      setTotalCount(searchResponse?.data?.totalCount);
    }
  }, [searchResponse]);

  useEffect(() => {
    if (!items.length) return;

    const findSearchData = (
      arr: unknown,
      searchText: string | number
    ): ISearchData | undefined => {
      if (!Array.isArray(arr)) return;
      return (arr as ISearchData[]).find(x => x.numericId.toString() === searchText || x.guidId === searchText);
    };

    items.forEach(e => {
      const lead = findSearchData(e.leadIDs, searchPayload.searchText);
      const deal = findSearchData(e.dealIDs, searchPayload.searchText);

      if (lead) {
        e.activeLeadGuid = lead.guidId;
        e.activeLeadID = lead.numericId;
      }

      if (deal) {
        e.activeDealGuid = deal.guidId;
        e.activeDealID = deal.numericId;
      }
    });
  }, [items]);

  const columns: GridColDef[] = [
    dataGridCol('source', {
      minWidth: 90,
      flex: 1.3,
    }),
    dataGridCol('numericId', {
      headerName: 'Contact Id',
      minWidth: 90,
      flex: 0.7,
    }),
    dataGridCol('activeLeadID', {
      headerName: 'Lead Id',
      minWidth: 90,
      flex: 0.7,
    }),
    dataGridCol('activeDealID', {
      headerName: 'Deal Id',
      minWidth: 90,
      flex: 0.7,
    }),
    dataGridCol('contactCreatedDate', {
      headerName: 'Lead Date',
      type: 'dateTime',
      minWidth: 90,
      flex: 1.1,
      valueGetter: ({ value }) => formatDate(value, { format: dateFormatWithTime }),
    }),
	dataGridCol('firstName', {
		minWidth: 90,
		flex: 0.8
	}),
    dataGridCol('lastName', {
      minWidth: 90,
      flex: 0.8,
    }),
    dataGridCol('contactUser', {
      minWidth: 90,
      flex: 1,
    }),
    dataGridCol('state', {
      minWidth: 90,
      flex: 0.5,
    }),
    dataGridCol('timeZone', {
      minWidth: 90,
      flex: 1,
    }),

    dataGridCol('cellPhone', {
      headerName: 'Phone Number',
      minWidth: 90,
      flex: 1.4,
      renderCell: (param) => formatPhoneNumber(param.row?.cellPhone || param.row?.homePhone || param.row?.workPhone),
    }),
    dataGridCol('loanAmount', {
      ...gridCurrencyCell,
      minWidth: 90,
      flex: 1,
    }),
    dataGridCol('assetTag', {
      minWidth: 90,
      flex: 1,
    }),
    dataGridCol('lastCommunicationDate', {
      headerName: 'Last Cmc Date',
      minWidth: 90,
      type: 'dateTime',
      flex: 1.1,
      valueFormatter: ({ value }) => formatDate(value, { format: dateFormatWithTime }),
    }),
    dataGridCol('status', {
      minWidth: 90,
      flex: 1.1,
    }),
    dataGridCol('lastStatusUpdate', {
      headerName: 'Last Stat Update',
      minWidth: 90,
      type: 'dateTime',
      flex: 1.1,
      valueFormatter: ({ value }) => formatDate(value, { format: dateFormatWithTime }),
    }),
    dataGridCol('lastCommentDate', {
      headerName: 'Last Cmt Date',
      minWidth: 90,
      type: 'dateTime',
      flex: 1.1,
      valueFormatter: ({ value }) => formatDate(value, { format: dateFormatWithTime }),
    }),
  ];

  const rowClick =  useCallback((row: SearchItem) => {
    let route = '';
    if (row.isMatchedOnDealId) {
      route = withQueryParams('/contact-manager', { id: row.id, tab: ContactManagerTabs.DEALS, dealId: row.matchedDealGuid || '' }, false);
    } else if (row.isMatchedOnLeadId) {
      route = withQueryParams('/contact-manager', { id: row.id, tab: ContactManagerTabs.LEADS, leadId: row.matchedLeadGuid || '' }, false);
    } else {
      route = withQueryParams('/contact-manager', { id: row.id, tab: ContactManagerTabs.CONTACTS, leadId: row.activeLeadGuid}, false);
    }
    const title = `${row.firstName} ${row.lastName}`;
    openTab({ title, route });
    navigate(route);
}, [items, requestPayload?.pageNumber]);

  const onSelectionModelChange = useCallback((model: GridSelectionModel) => {
    model && setSelectionModel(model);
  }, []);

  return (
    <ViewHeightWrapper>
      <PhoenixGrid
        strict
        noButton
        withHeader
        disableSelectionOnClick
        showClearAllFilter
        selectionModel={selectionModel}
        name={title}
        columns={columns}
        loading={(!items && !error) || isValidating}
        rows={items && !isValidating ? items : []}
        rowCount={totalCount}
        pageSize={pageSize}
        paginationMode="server"
        sortingMode='server'
        filterMode='server'
        getRowId={(row) => row.id}
        onSortModelChange={onSortModelChange}
        onSelectionModelChange={onSelectionModelChange}
        onFilterModelChange={onFilterModelChange}
        onPageChange={onPageChange}
        onRowClick={({ row }: { row: SearchItem }) => rowClick(row)}
      />
    </ViewHeightWrapper>
  );
};

